import React, { useCallback, useContext } from 'react';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { usePopoverOnInteraction } from '../../hooks/usePopoverOnInteraction';
import PopperPositionedPopover from '../../PopperPositionedPopover';
import DefaultPopoverContent from '../../_partials/DefaultPopoverContent';
import type { PopperPositioningProps } from '../../types';

export interface Props extends PopperPositioningProps {
  hoverDelay?: number;
  children?: React.ReactNode;
  popoverContent?: React.ReactNode;
  showArrow?: boolean;
  noPadding?: boolean;
  className?: string;
  arrowClassName?: string;
  wrapperClassName?: string;
  contentClassName?: string;
  onVisible?: () => void;
}
/** Please note that this component is not DS-conforming */
const SimpleHoverPopperPopover: React.FC<Props> = ({
  children,
  popoverContent,
  positionFixed,
  eventsEnabled,
  referenceElement,
  hoverDelay,
  placement,
  secondaryPlacement,
  showArrow = true,
  className,
  arrowClassName,
  contentClassName,
  wrapperClassName,
  noPadding,
  isVisible: isVisibleProp = true,
  onVisible,
}) => {
  const isMobile = useContext(MobileContext);
  const { isVisible, onClick, onMouseEnter, onMouseLeave } = usePopoverOnInteraction({
    listenOnHover: !isMobile,
    hoverDelay,
    onVisible,
  });

  const render = useCallback(
    ({
      effectivePlacement,
      effectiveSecondaryPlacement,
      contentStyle,
      arrowStyle,
      contentAttributes,
      arrowAttributes,
      rootRef,
      arrowRef,
      updatePositioning,
    }) => {
      return (
        <DefaultPopoverContent
          effectivePlacement={effectivePlacement}
          effectiveSecondaryPlacement={effectiveSecondaryPlacement}
          rootRef={rootRef}
          arrowRef={arrowRef}
          contentStyle={contentStyle}
          arrowStyle={arrowStyle}
          contentAttributes={contentAttributes}
          arrowAttributes={arrowAttributes}
          updatePositioning={updatePositioning}
          showArrow={showArrow}
          contentClassName={contentClassName}
          arrowClassName={arrowClassName}
          wrapperClassName={wrapperClassName}
          noPadding={noPadding}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {popoverContent}
        </DefaultPopoverContent>
      );
    },
    [
      arrowClassName,
      contentClassName,
      noPadding,
      onMouseEnter,
      onMouseLeave,
      popoverContent,
      showArrow,
      wrapperClassName,
    ]
  );

  return (
    <PopperPositionedPopover
      usePortal
      placement={placement}
      secondaryPlacement={secondaryPlacement}
      isVisible={isVisibleProp && isVisible}
      positionFixed={positionFixed}
      eventsEnabled={eventsEnabled}
      referenceElement={referenceElement}
      className={className}
      renderPopover={render}
      onReferenceClick={onClick}
      onReferenceMouseEnter={onMouseEnter}
      onReferenceMouseLeave={onMouseLeave}
    >
      {children}
    </PopperPositionedPopover>
  );
};
SimpleHoverPopperPopover.displayName = 'SimpleHoverPopperPopover';

export default SimpleHoverPopperPopover;
